import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// One-time cleanup: dismiss suppression is now server-authoritative (the silence endpoint, ~2h personal).
// The card no longer persists `dismissedBucketEnd`, so the legacy persisted key is dead — clear it to
// avoid orphaned localStorage state.
if (typeof localStorage !== 'undefined') localStorage.removeItem('risk-recognition');

/**
 * Slice de sessão do risco (estado client-side específico do tipo). O estado de drawer (qual tipo/payload
 * está aberto) saiu para o store genérico `useNotificationDrawerStore`; aqui ficam só os bits do risco:
 *
 * - `dismissedBucketEnd`: in-memory only. O servidor (silence endpoint) é a fonte de verdade da supressão;
 *   isto só gateia re-render na sessão pra o card ficar escondido após a animação. Um reload pode reexibir
 *   até a próxima janela (~10 min) refletir o silence.
 * - `routeFollowBucketEnd`: bucket que o usuário escolheu seguir até /tracking via "Ver trajeto". In-memory.
 * - `lastDisplayedBucketEnd`: dispara o evento "Displayed" uma vez por bucket.
 */
interface RiskRecognitionSliceState {
  lastDisplayedBucketEnd: string | null;
  dismissedBucketEnd: string | null;
  routeFollowBucketEnd: string | null;
  markDisplayed: (bucketEnd: string) => void;
  // O highlight vem do feed state do Knock, então os callers passam o bucket end acionado.
  dismiss: (bucketEnd: string | null) => void;
  followRouteToTracking: (bucketEnd: string | null) => void;
  reset: () => void;
}

const initialState = { lastDisplayedBucketEnd: null, dismissedBucketEnd: null, routeFollowBucketEnd: null } as const;

const useRiskRecognitionSlice = create<RiskRecognitionSliceState, [['zustand/devtools', never]]>(
  devtools(
    (set) => ({
      ...initialState,
      markDisplayed: (bucketEnd) => set({ lastDisplayedBucketEnd: bucketEnd }, false, 'markDisplayed'),
      dismiss: (bucketEnd) => set({ dismissedBucketEnd: bucketEnd, routeFollowBucketEnd: null }, false, 'dismiss'),
      followRouteToTracking: (bucketEnd) => set({ routeFollowBucketEnd: bucketEnd }, false, 'followRouteToTracking'),
      reset: () => set({ ...initialState }, false, 'reset'),
    }),
    { name: 'useRiskRecognitionSlice' }
  )
);

export default useRiskRecognitionSlice;
